import { FC, useRef, useState, ChangeEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { generateUniqueId, getFileExtension } from "@/utils/string.util";
import { isImageSupported } from "@/utils/image.util";
import { toast } from "react-toastify";
import styles from "./VerificationPhotoDialog.module.scss";
import { InspectionLocationMap } from "@/components/maps/InspectionLocationMap";

export interface CapturedVerificationImage {
    id: string;
    name: string;
    data: string;
    contentType: string;
}

type MapLatLng = { lat: number; lng: number };

interface VerificationPhotoDialogProps {
    distanceMetres?: number | null;
    registeredCoords?: MapLatLng | null;
    currentCoords?: MapLatLng | null;
    currentAccuracyMetres?: number | null;
    onConfirm: (images: CapturedVerificationImage[]) => void;
    onCancel: () => void;
}

export const VerificationPhotoDialog: FC<VerificationPhotoDialogProps> = ({
    distanceMetres,
    registeredCoords: _registeredCoords,
    currentCoords,
    currentAccuracyMetres,
    onConfirm,
    onCancel,
}) => {
    const { t } = useTranslation();
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [images, setImages] = useState<CapturedVerificationImage[]>([]);

    const handleCapture = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!isImageSupported(file)) {
            toast.error(t("verification_unsupported_format"));
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const id = generateUniqueId();
            setImages((prev) => [
                ...prev,
                {
                    id,
                    name: `${id}${getFileExtension(file) || ".jpg"}`,
                    data: reader.result as string,
                    contentType: file.type || "image/jpeg",
                },
            ]);
        };
        reader.readAsDataURL(file);

        if (cameraInputRef.current) {
            cameraInputRef.current.value = "";
        }
    };

    const handleRemove = (id: string) => {
        setImages((prev) => prev.filter((img) => img.id !== id));
    };

    const handleConfirm = () => {
        if (images.length === 0) return;
        onConfirm(images);
    };

    const hasDistance =
        typeof distanceMetres === "number" && Number.isFinite(distanceMetres);

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <h2 className={styles.title}>
                    {t("help_confirm_factory_info")}
                </h2>

                {!hasDistance ? (
                    <p className={styles.distanceInfo}>
                        {t("verification_location_unavailable_warning")}
                    </p>
                ) : null}
                <p className={styles.message}>{t("title_summary")}</p>

                <InspectionLocationMap
                    registered={null}
                    current={currentCoords}
                    currentAccuracyMetres={currentAccuracyMetres}
                />

                <p className={styles.message}>
                    <Trans
                        i18nKey="verification_take_photo_message"
                        components={{
                            highlight: <strong className={styles.messageHighlight} />,
                        }}
                    />
                </p>
                <p className={styles.message}>{t("Suggestions")}</p>

                {images.length > 0 && (
                    <div className={styles.thumbnailGrid}>
                        {images.map((img) => (
                            <div key={img.id} className={styles.thumbnailWrapper}>
                                <img
                                    src={img.data}
                                    alt=""
                                    className={styles.thumbnail}
                                />
                                <button
                                    type="button"
                                    className={styles.removeBtn}
                                    onClick={() => handleRemove(img.id)}
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <button
                    type="button"
                    className={styles.captureButton}
                    onClick={() => cameraInputRef.current?.click()}
                >
                    📷 {images.length === 0 ? t("verification_take_photo") : t("verification_take_another")}
                </button>

                <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={handleCapture}
                />

                <div className={styles.actions}>
                    <button
                        type="button"
                        className={`${styles.actionButton} ${styles.secondary}`}
                        onClick={onCancel}
                    >
                        {t("Back")}
                    </button>
                    <button
                        type="button"
                        className={`${styles.actionButton} ${styles.primary}`}
                        disabled={images.length === 0}
                        onClick={handleConfirm}
                    >
                        {t("verification_confirm")}
                    </button>
                </div>
            </div>
        </div>
    );
};
